import {
  startOfMonth,
  endOfMonth,
  isSameMonth,
  format,
  startOfWeek,
  endOfWeek,
  eachWeekOfInterval,
  addMonths,
  differenceInCalendarMonths,
} from "date-fns";
import { useAtomValue } from "jotai";
import React from "react";

import { Box, Text } from "@whoosh/design-system/src/components/primitive";
import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";

import { getCalendarSettingsForDateAtom } from "../../Datepickers/components/Calendar/atoms";
import { CalendarBodyProps } from "../types";
import { getParsedDateObj } from "../utils/calendarDateUtils";
import { MonthHeader } from "./MonthHeader";
import { WeekRow } from "./WeekRow";

type CalendarMonthBaseProps = Omit<CalendarBodyProps, "onSelectDate">;

interface CalendarMonthProps extends CalendarMonthBaseProps {
  onDatePress: (date: string) => void;
  baseMonthDateObj: Date;
  selectedDate?: string;
  displayHorizontally?: boolean;
  isLeft?: boolean;
  isRight?: boolean;
  clubWeekStartsOn: NumericalDayOfWeek;
}

export const CalendarMonth = (props: CalendarMonthProps) => {
  const {
    displayHorizontally,
    endDate,
    isLeft,
    isRight,
    baseMonthDateObj,
    onDatePress,
    selectedDate,
    startDate,
    clubWeekStartsOn,
  } = props;

  const startDateObj = getParsedDateObj(startDate);

  const calendarStartDate =
    differenceInCalendarMonths(baseMonthDateObj, startDateObj) < 1
      ? startOfWeek(startDateObj, {
          weekStartsOn: clubWeekStartsOn,
        })
      : startOfMonth(baseMonthDateObj);

  const nextMonthObj = addMonths(baseMonthDateObj, 1);
  const firstDayOfNextMonth = startOfMonth(nextMonthObj);
  const getCalendarSettingsForDate = useAtomValue(
    getCalendarSettingsForDateAtom
  );
  const { showDate: showNextMonth } = getCalendarSettingsForDate(
    firstDayOfNextMonth,
    baseMonthDateObj
  );

  const isFirstMonthVisible = isSameMonth(startDateObj, baseMonthDateObj);

  const getMonthName = (monthObj: Date) => format(monthObj, "MMMM yyyy");

  const getFirstMonthWeeks = () => {
    const effectiveStartDate = calendarStartDate;
    const monthEnd = endOfMonth(baseMonthDateObj);
    const calendarEnd = endOfWeek(monthEnd, {
      weekStartsOn: clubWeekStartsOn,
    });

    return eachWeekOfInterval(
      { start: effectiveStartDate, end: calendarEnd },
      { weekStartsOn: clubWeekStartsOn }
    );
  };

  const getNextMonthWeeks = () => {
    if (!isFirstMonthVisible || !showNextMonth || displayHorizontally) {
      return [];
    }

    const monthStart = startOfMonth(nextMonthObj);
    const monthEnd = endOfMonth(nextMonthObj);
    const calendarStart = startOfWeek(monthStart, {
      weekStartsOn: clubWeekStartsOn,
    });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: clubWeekStartsOn });

    return eachWeekOfInterval(
      { start: calendarStart, end: calendarEnd },
      { weekStartsOn: clubWeekStartsOn }
    );
  };

  const firstMonthWeeks = getFirstMonthWeeks();
  const nextMonthWeeks = getNextMonthWeeks();
  const showNextMonthBelow = nextMonthWeeks.length > 0;

  return (
    <Box flex={1} pl={!!isRight ? 10 : 0} pr={!!isLeft ? 10 : 0}>
      <>
        <Text variant="mainSmallSemiBold" pb={6}>
          {getMonthName(baseMonthDateObj)}
        </Text>
        <MonthHeader clubWeekStartsOn={clubWeekStartsOn} />
      </>

      {firstMonthWeeks.map((weekStart, i) => (
        <WeekRow
          endDate={endDate}
          key={`week${i}`}
          monthDateObj={baseMonthDateObj}
          onDatePress={onDatePress}
          selectedDate={selectedDate}
          startDateObj={weekStart}
          clubWeekStartsOn={clubWeekStartsOn}
        />
      ))}

      {showNextMonthBelow ? (
        <>
          <Box mt={4} />
          <Text variant="mainSmallSemiBold" pb={6} pl={0} color="text-primary">
            {getMonthName(nextMonthObj)}
          </Text>

          {nextMonthWeeks.map((weekStart, j) => (
            <WeekRow
              endDate={endDate}
              key={`week-following-month-${j}`}
              monthDateObj={nextMonthObj}
              onDatePress={onDatePress}
              selectedDate={selectedDate}
              startDateObj={weekStart}
              clubWeekStartsOn={clubWeekStartsOn}
            />
          ))}
        </>
      ) : null}
    </Box>
  );
};
